import React from "react";
import { Modal, Form, Select, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import request from "../../../utils/request";
import { ProjectItem, useGlobal } from "../../../contexts/GlobalContext";

interface KnowledgeUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  projects: ProjectItem[];
}

const KnowledgeUploadModal: React.FC<KnowledgeUploadModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  projects,
}) => {
  const [form] = Form.useForm();
  const { API_BASE_URL, token, user } = useGlobal();

  React.useEffect(() => {
    // 设置默认项目为第一个 PostChat 项目
    if (visible && projects.length > 0) {
      const postChatProjects = projects.filter(project => project.is_postchat);
      if (postChatProjects.length > 0) {
        form.setFieldsValue({ project_id: postChatProjects[0].id });
      }
    }
  }, [visible, projects, form]);

  const acceptedFileTypes = ".txt,.html,.md,.docx";

  const beforeUpload = (file: File) => {
    const isAcceptedType = [".txt", ".html", ".md"].some((type) =>
      file.name.toLowerCase().endsWith(type)
    );

    if (!isAcceptedType) {
      message.error(
        `${file.name} 不是支持的文件格式，仅支持 TXT、HTML、MD 格式`
      );
    }

    return isAcceptedType || Upload.LIST_IGNORE;
  };

  const handleUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();

    try {
      // 检查用户是否是 PostChat 会员
      if (!user?.apiInfo?.postchat?.vip_info?.is_vip) {
        message.error("此功能为全功能项目（Pro 及以上）能力");
        onError();
        return;
      }

      // 获取表单数据
      const values = await form.validateFields();

      formData.append("file", file);
      formData.append("project_id", values.project_id);
      // 从文件名中提取标题（去除扩展名）
      const title = file.name.replace(/\.[^/.]+$/, "");
      formData.append("title", title);

      const response = await request.post(
        `${API_BASE_URL}/api/v3/knowledge/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.code === 200) {
        onSuccess();
        message.success(`${file.name} 上传成功`);
        onCancel();
        onSuccess();
      } else {
        onError();
        message.error(response.data.msg || `${file.name} 上传失败`);
      }
    } catch (error) {
      onError();
      message.error(`${file.name} 上传失败`);
    }
  };

  return (
    <Modal title="上传文件" open={visible} onCancel={onCancel} footer={null}>
      <Form form={form} layout="vertical">
        <Form.Item
          name="project_id"
          label="选择项目"
          rules={[{ required: true, message: "请选择项目" }]}
          extra="仅支持 PostChat 类型的项目"
        >
          <Select placeholder="请选择项目">
            {projects.filter(project => project.is_postchat).map((project) => (
              <Select.Option key={project.id} value={project.id}>
                {project.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Upload.Dragger
          name="files"
          multiple
          maxCount={10}
          accept={acceptedFileTypes}
          beforeUpload={beforeUpload}
          customRequest={handleUpload}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
          <p className="ant-upload-hint" style={{ color: "#888" }}>
            支持单次或批量上传，最多可上传10个文件
            <br />
            支持的文件格式：TXT、HTML、MD 、DOCX
          </p>
        </Upload.Dragger>
      </Form>
    </Modal>
  );
};

export default KnowledgeUploadModal;
